import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { DatePicker, Form, Input, InputNumber, Select } from 'antd';

import { InfoCircleOutlined } from '@ant-design/icons';
import equal from 'fast-deep-equal';

import messages from '../../constants/messages';

function CreateMeasurementsForm({ className, formRef, onChange, setSubmittable }) {
  const [form] = Form.useForm();

  const { types: measurementTypes } = useSelector(state => state.measurement);

  const [initialFormValues, setInitialFormValues] = useState({});
  const [majorMeasurementTypeOptions, setMajorMeasurementTypeOptions] = useState([]);
  const [minorMeasurementTypeOptions, setMinorMeasurementTypeOptions] = useState([]);
  const [selectedMajorMeasurementType, setSelectedMajorMeasurementType] = useState();

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  useEffect(() => {
    const initial = form.getFieldsValue(true);
    setInitialFormValues(initial);
  }, []);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable?.(true))
      .catch(() => setSubmittable?.(false));
  }, [values]);

  useEffect(() => {
    if (measurementTypes) {
      const options = measurementTypes.map(measurementType => ({
        label: measurementType.major_measurement_name,
        value: measurementType.major_measurement_id
      }));

      setMajorMeasurementTypeOptions(options);
    }
  }, [measurementTypes]);

  useEffect(() => {
    if (measurementTypes && selectedMajorMeasurementType) {
      const selectedType = measurementTypes.find(type => type.major_measurement_id === selectedMajorMeasurementType);
      const options = selectedType.measure_minors?.map(measurementType => ({
        label: measurementType.minor_measurement_name,
        value: measurementType.minor_measurement_id,
        uom: measurementType.uom
      }));

      setMinorMeasurementTypeOptions(options);
    }
  }, [selectedMajorMeasurementType]);

  const handleMajorMeasurementTypeChange = newValue => {
    setSelectedMajorMeasurementType(newValue);
  };

  const handleMinorMeasurementTypeChange = newValue => {
    const selectedMinorMeasurementType = minorMeasurementTypeOptions.find(option => option.value === newValue);
    form.setFieldsValue({ uom: selectedMinorMeasurementType?.uom || '' });
  };

  function handleChange(changedValues, allValues) {
    // if major_mesure is changed - connected metrics should be nullified
    if (changedValues.major_measure_id && allValues.minor_measure_id) {
      form.setFieldsValue({
        minor_measure_id: null,
        uom: null,
        measurement: null
      });
    }

    const isSame = equal(allValues, initialFormValues);
    onChange?.(!isSame);
  }

  return (
    <Form
      autoComplete="off"
      layout="horizontal"
      variant="outlined"
      className={className}
      form={form}
      requiredMark={true}
      onValuesChange={handleChange}
      labelCol={{ span: 8 }} 
      wrapperCol={{ span: 16 }}
      labelAlign="left"
    >
      <Form.Item
        label="Major Measurement Type"
        name="major_measure_id"
        rules={[{ required: true, message: messages.FIELD_REQUIRED('Major Measurement Type') }]}>
        <Select
          placeholder="Select Major Measurement Type"
          options={majorMeasurementTypeOptions}
          onChange={handleMajorMeasurementTypeChange}
        />
      </Form.Item>

      {values && values.major_measure_id && (
        <>
          <Form.Item
            label="Minor Measurement Type"
            name="minor_measure_id"
            rules={[{ required: true, message: messages.FIELD_REQUIRED('Minor Measurement Type') }]}>
            <Select
              placeholder="Select Minor Measurement Type"
              disabled={!selectedMajorMeasurementType && !minorMeasurementTypeOptions}
              options={minorMeasurementTypeOptions}
              onChange={handleMinorMeasurementTypeChange}
            />
          </Form.Item>

          <Form.Item
            label="UOM"
            name="uom"
            tooltip={{ title: 'This is read only field. You can not change it.', icon: <InfoCircleOutlined /> }}>
            <Input disabled readOnly />
          </Form.Item>

          <Form.Item
            label="Measurement"
            name="measurement"
            validateDebounce={1000}
            rules={[
              {
                required: true,
                message: messages.FIELD_REQUIRED('Measurement')
              },
              {
                type: 'number',
                message: messages.FIELD_INVALID('Measurement')
              }
            ]}>
            <InputNumber className="w-full" placeholder="0" />
          </Form.Item>

          <Form.Item
            label="Measurement Date"
            name="measurement_date"
            rules={[{ required: true, message: messages.FIELD_REQUIRED('Measurement Date') }]}>
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item label="Comments" name="comments">
            <Input.TextArea placeholder="Left your comments here" />
          </Form.Item>
        </>
      )}
    </Form>
  );
}

export default memo(CreateMeasurementsForm);
